// The console's side of the shared `?from=&to=` date filter.
//
// The SHAPE and the day arithmetic live in `@recruiter/shared`
// (`components/ui/date-range`), because the control renders them; this module
// is the three things only the console needs: putting a range on the wire,
// keying a query cache by it, and reading one back out of a URL.
//
// Every list this filters is keyset-paged, so the range narrows SERVER-side —
// a browser-side filter could only ever cover the pages already fetched, which
// is the same reason `q`, `surfaces` and the rest are server-side. The one
// exception is the Talent dashboard, which holds its whole snapshot in memory
// and folds the range with `isInDateRange` instead.
import { type DateRange, EMPTY_DATE_RANGE, isIsoDay, normalizeDateRange } from "@recruiter/shared";

/** Write a range onto an outgoing query string. Absent bounds write nothing,
 *  so an unfiltered read is the bare URL it has always been. */
export function appendDateRange(params: URLSearchParams, range: DateRange | undefined): void {
  if (!range) return;
  if (range.from) params.set("from", range.from);
  if (range.to) params.set("to", range.to);
}

/** A range as one stable string, for a TanStack Query key.
 *
 *  The range MUST be part of every key it narrows: it filters server-side, so
 *  two ranges are two different lists — each with its own keyset cursor — not
 *  one list rendered two ways. Keying without it would serve the previous
 *  range's rows under the new range's heading.
 *
 *  `-` for an unbounded side rather than an empty string, so `from` set and
 *  `to` set cannot produce the same key. */
export function dateRangeKey(range: DateRange | undefined): string {
  if (!range) return "-:-";
  return `${range.from ?? "-"}:${range.to ?? "-"}`;
}

/** A `?from=` / `?to=` URL param: a real ISO day, or absent.
 *
 *  Anything else is DROPPED rather than passed through. These are shareable
 *  links, so a truncated paste or a hand-edit is a real input, and the backend
 *  422s a malformed day — turning a mangled URL into an error page instead of
 *  the list somebody expected. Dropping degrades to "unfiltered", which is
 *  visibly wrong in a way the reader can see and fix. */
export function parseDayParam(value: unknown): string | undefined {
  return isIsoDay(value) ? value : undefined;
}

/** Build the range a page reads from its two URL params. */
export function rangeFromSearch(from: unknown, to: unknown): DateRange {
  const range = normalizeDateRange({
    from: parseDayParam(from) ?? null,
    to: parseDayParam(to) ?? null,
  });
  return range.from === null && range.to === null ? EMPTY_DATE_RANGE : range;
}

/** The search-param patch that puts a range INTO a URL.
 *
 *  A cleared bound becomes `undefined`, which drops the key entirely rather
 *  than leaving `?from=` behind: the plain link is the one people share, and
 *  an empty param is noise in it. */
export function searchForRange(range: DateRange): { from?: string; to?: string } {
  return { from: range.from ?? undefined, to: range.to ?? undefined };
}

export type { DateRange };
